import React, { useState, useContext, useRef, useEffect } from "react";
import { useIntl } from "react-intl";
import Webcam from "react-webcam";
import useResponsive from "../../core/hooks/useResponsive";

import ConfirmModalContainer from "../ConfirmModalContainer/ConfirmModalContainer";
import { Modal, Button, Text } from "react_reusable_components";
import { GlobalLoaderContext } from "../../globalContext/globalLoader/globalLoaderProvider";
import {
  formatDuration,
  getConditionalContent,
  getLocalizedMessage,
} from "../../UtilityFunctions/utils";
import MediaService from "../../services/media-service";
import variables from "../../themes/base/styles/variables";
import VideoPlayer from "../../components/VideoPlayer";
import moment from "moment";
import { useParams } from "react-router-dom";
import AuditService from "../../services/audit-service";
import { FACING_MODE } from "../../constants";
import { AuditContext } from "../../globalContext/ManageAudit/AuditProvider";
import useOffline from "../../hooks/useOffline";
import { isMultipleCameraDevice } from "../../UtilityFunctions/utils";
import { setMultipleCamera } from "../../globalContext/ManageAudit/auditActions";

const PreviewCapturingStyle = {
  webCamStyle: {
    height: variables.fullHeight,
    backgroundColor: "transparent",
    position: "relative",
    objectFit: "contain",
  },
  modalBodyStyle: {
    width: variables.fullWidth,
    height: "calc(100vh - 12.5rem)",
    backgroundColor: "#000",
  },
  modalMaskStyle: {
    backdropFilter: variables.highBlur,
    background: variables.blurBackground,
  },
  btnWrapperStyle: {
    position: "absolute",
    bottom: "2rem",
    left: "50%",
    transform: "translateX(-50%)",
    gap: variables.gapMediumSmall,
    display: variables.dFlex,
    alignItems: variables.alignCenter,
  },
  flagBtnStyle: {
    border: `1px solid  ${variables.grey4}`,
    color: variables.textDefault,
    fontSize: variables.fontSizeNormal,
    fontWeight: variables.fontWeightNormal,
    alignItems: "center",
    display: "flex",
    padding: `${variables.paddingSmallest} ${variables.paddingSmall}`,
    backgroundColor: variables.bodyBg,
  },
  mainBtnStyle: {
    gap: variables.gapXS,
    display: "flex",
    alignItems: "center",
  },
  videoRecordingContainerStyle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    position: "absolute",
    top: 0,
    left: "50%",
    transform: "translateX(-50%)",
    padding: variables.paddingSmallest,
    marginTop: variables.marginMedium,
    borderRadius: "50px",
    background: "rgba(0, 0, 0, 0.44)",
    backdropFilter: "blur(7px)",
    width: "fit-content",
    color: variables.bodyBg,
    fontSize: variables.fontSizeNormal,
    lineHeight: variables.lineHeightNormal,
    gap: variables.gapXS,
  },
  recordingIconStyle: {
    width: variables.iconWidthLarge,
    height: variables.iconHeightLarge,
  },
  upperWrapperStyle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    position: "absolute",
    top: 0,
    left: "50%",
    transform: "translateX(-50%)",
    width: variables.fullWidth,
    padding: variables.paddingMedium,
  },
  closeIconStyle: {
    width: variables.iconWidthLarge,
    height: variables.iconHeightLarge,
    cursor: "pointer",
    backgroundColor: variables.opaqueBackground,
    borderRadius: "50%",
    padding: variables.paddingQuarter,
  },
  iconStyle: {
    width: variables.iconWidthLarge,
    height: variables.iconHeightLarge,
  },
};
const PreviewCapturing = ({
  setMediaUploaded,
  setStartCamera,
  mediaUploaded,
  isVideoCapturing,
  preview,
  setPreview,
  mediaUrl,
  selectedMedia,
  questionId,
  onMediaChanged,
  fetchResponseId,
  responseId,
}) => {
  const webcamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const intervalRef = useRef(null);
  const chunksRef = useRef([]);
  const loaderDispatch = useContext(GlobalLoaderContext)[1];
  const intl = useIntl();
  const responsive = useResponsive();

  const [facingMode, setFacingMode] = useState(FACING_MODE.ENVIRONMENT);
  const [disableButton, setDisableButton] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [duration, setDuration] = useState(0);
  const [isRedFlagClicked, setIsRedFlagClicked] = useState(false);
  const [isGreenFlagClicked, setIsGreenFlagClicked] = useState(false);
  const [auditState, auditDispatch] = useContext(AuditContext);
  const [isWebStream, setIsWebStream] = useState(false);
  const isOffline = useOffline();

  const handleUserMedia = React.useCallback(() => {
    setIsWebStream(true);
  }, []);
  useEffect(() => {
    if (!auditState.isCameraPermissionChecked && !preview) {
      isMultipleCameraDevice(auditDispatch, setMultipleCamera, facingMode);
    }
  }, []);
  const chunkIntervalRef = useRef(null);

  const statusRef = useRef("started");
  const documentId = useRef();

  const greenFlagRef = useRef([]);
  const redFlagRef = useRef([]);
  const { id, auditId } = useParams();

  const handleUserMediaError = () => {
    setDisableButton(true);
    setOpenModal(true);
    setIsWebStream(false);
  };

  useEffect(() => {
    if (isOffline && isRecording) {
      stopRecording();
    }
  }, [isOffline]);

  const captureVideoRecording = () => {
    setIsRecording(true);
    const options = { mimeType: "video/webm;codecs=vp8" };
    if (
      !!window.chrome &&
      (!!window.chrome.webstore || !!window.chrome.runtime)
    ) {
      mediaRecorderRef.current = new MediaRecorder(
        webcamRef.current.stream,
        options
      );
    } else {
      mediaRecorderRef.current = new MediaRecorder(webcamRef.current.stream, {
        mimeType: "video/webm",
      });
    }

    mediaRecorderRef.current.ondataavailable = handleDataAvailable;

    chunksRef.current = [];
    mediaRecorderRef.current.start();
    captureMedia();
    chunkIntervalRef.current = setInterval(() => {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.start();
    }, 10000);

    intervalRef.current = setInterval(() => {
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state === "recording"
      ) {
        setDuration((prevDuration) => prevDuration + 1);
      }
    }, 1000);
  };

  const stopRecording = () => {
    statusRef.current = "completed";
    mediaRecorderRef.current.stop();
    if (isVideoCapturing) {
      setIsRecording(false);
    }
    setStartCamera(false);
    clearInterval(chunkIntervalRef.current);
    clearInterval(intervalRef.current);
  };

  const handleDataAvailable = (event) => {
    if (event.data.size > 0) {
      if (statusRef.current === "started") {
        statusRef.current = "in_progress";
      }
      chunksRef.current.push(event.data);
      captureMedia(event.data);
    }
  };

  const captureMedia = (chunk) => {
    if (!chunk) {
      MediaService.uploadMultipartMedia({
        body: new FormData(),
        queryParams: {
          type: "VIDEO",
          auditId: id || auditId,
          questionId,
          status: "started",
        },
        intl,
      }).then(async (res) => {
        statusRef.current = "started";
        if (res?.data?.data?.documentId) {
          documentId.current = res.data.data.documentId;
          fetchResponseId(res?.data?.data?.responseId);
        }
      });
      return;
    }
    const mediaType = "VIDEO";
    const blob = new Blob([chunk], { type: "video/webm" });
    let mediaUrlData = URL.createObjectURL(blob);

    fetch(mediaUrlData)
      .then((res) => res.blob())
      .then((blob) => {
        const mediaFile = new File([blob], moment.now(), {
          type: isVideoCapturing ? "video/webm" : "image/png",
          lastModified: new Date().getTime(),
        });
        const formData = new FormData();
        formData.append("file", mediaFile);
        formData.append("fileName", moment.now());
        formData.append("type", mediaType);
        MediaService.uploadMultipartMedia({
          body: formData,
          queryParams: {
            type: mediaType,
            auditId: id || auditId,
            questionId,
            status: statusRef.current,
            documentId: documentId.current,
          },
          intl,
        })
          .then(async (res) => {
            if (res?.data?.data?.documentId) {
              documentId.current = res.data.data.documentId;
              fetchResponseId(res?.data?.data?.responseId);
            }

            if (statusRef.current === "completed") {
              const newMedia = isVideoCapturing
                ? {
                    documentId: documentId.current,
                    greenFlag: greenFlagRef.current,
                    redFlag: redFlagRef.current,
                  }
                : res?.data?.data?.id;
              const newMedias = mediaUploaded?.length
                ? [...mediaUploaded, newMedia]
                : [newMedia];
              fetchResponseId(res?.data?.data?.responseId);
              setMediaUploaded(newMedias);
              onMediaChanged(newMedias);
            }
          })
          .catch(() => {});
      });
  };

  const captureSnapshotMedia = () => {
    const mediaType = "SNAPSHOT";
    let mediaUrlData = webcamRef?.current?.getScreenshot();
    fetch(mediaUrlData)
      .then((res) => res.blob())
      .then((blob) => {
        const mediaFile = new File([blob], moment.now(), {
          type: isVideoCapturing ? "video/webm" : "image/png",
          lastModified: new Date().getTime(),
        });
        const formData = new FormData();
        formData.append("file", mediaFile);
        formData.append("fileName", moment.now());
        formData.append("type", mediaType);
        MediaService.uploadMedia({
          loaderDispatch,
          body: formData,
          queryParams: {
            type: mediaType,
            auditId: id || auditId,
            questionId,
          },
          intl,
        }).then(async (res) => {
          if (res?.data?.data?.documentId) {
            documentId.current = res.data.data.documentId;
          }
          const newMedia = isVideoCapturing
            ? {
                documentId: documentId.current,
                greenFlag: greenFlagRef.current,
                redFlag: redFlagRef.current,
              }
            : res?.data?.data?.id;
          const newMedias = mediaUploaded?.length
            ? [...mediaUploaded, newMedia]
            : [newMedia];
          fetchResponseId(res?.data?.data?.responseId);
          setMediaUploaded(newMedias);
          onMediaChanged(newMedias);
          setStartCamera(false);
        });
      });
  };

  const captureMediaHandleClick = () => {
    if (isVideoCapturing) {
      if (isRecording) {
        stopRecording();
      } else {
        captureVideoRecording();
      }
    } else {
      captureSnapshotMedia();
    }
  };

  async function onFlagClick(flag) {
    if (flag === "green") {
      setIsGreenFlagClicked(true);
      greenFlagRef.current.push(`${duration}`);
      setTimeout(() => setIsGreenFlagClicked(false), 1000);
    }
    if (flag === "red") {
      setIsRedFlagClicked(true);
      redFlagRef.current.push(`${duration}`);
      setTimeout(() => setIsRedFlagClicked(false), 1000);
    }
    await AuditService.updateFlags({
      intl,
      documentId: documentId.current,
      body: {
        redFlag: redFlagRef.current,
        greenFlag: greenFlagRef.current,
        auditResponseId: responseId,
      },
    });
  }
  return (
    <>
      <Modal
        maskClosable={false}
        disableGutter
        bodyStyle={PreviewCapturingStyle.modalBodyStyle}
        centered
        open={true}
        closable={false}
        footer={null}
        width={window.innerWidth - (responsive.isSm ? 232 : 30)}
        maskStyle={PreviewCapturingStyle.modalMaskStyle}
      >
        {!preview && (
          <Webcam
            audio={!!isVideoCapturing}
            mirrored={facingMode === FACING_MODE.USER}
            style={{
              ...PreviewCapturingStyle.webCamStyle,
              // ...(facingMode === FACING_MODE.ENVIRONMENT
              //   ? { transform: "scaleX(-1)" }
              //   : {}),
            }}
            width={window.innerWidth - (responsive.isSm ? 232 : 30)}
            ref={webcamRef}
            videoConstraints={{ facingMode }}
            onUserMedia={handleUserMedia}
            onUserMediaError={handleUserMediaError}
          />
        )}
        {isVideoCapturing && preview && (
          <VideoPlayer mediaUrl={mediaUrl} mediaToPreview={selectedMedia} />
        )}
        {preview && !isVideoCapturing && (
          <img
            src={mediaUrl}
            alt="snapshotPreview"
            width={window.innerWidth - (responsive.isSm ? 232 : 30)}
            style={PreviewCapturingStyle.webCamStyle}
          />
        )}

        {!preview && (
          <div style={PreviewCapturingStyle.btnWrapperStyle}>
            {isRecording && (
              <>
                <Button
                  style={{
                    ...PreviewCapturingStyle.flagBtnStyle,
                    ...(isRedFlagClicked && {
                      border: `2px solid  ${variables.primaryBg}`,
                    }),
                  }}
                  ghost
                  icon={<img src={"/icons/flag.svg"} alt="flag" />}
                  onClick={() => onFlagClick("red")}
                >
                  {responsive.isSm &&
                    getLocalizedMessage(intl, "evidence.redFlag")}
                </Button>
                <Button
                  style={{
                    ...PreviewCapturingStyle.flagBtnStyle,
                    ...(isGreenFlagClicked && {
                      border: `2px solid  ${variables.successGreen}`,
                    }),
                  }}
                  ghost
                  icon={<img src={"/icons/flag_green.svg"} alt="flag" />}
                  onClick={() => onFlagClick("green")}
                >
                  {responsive.isSm &&
                    getLocalizedMessage(intl, "evidence.greenFlag")}
                </Button>
              </>
            )}
            {auditState?.isMultipleCamera && isWebStream && !isRecording && (
              <Button
                style={{
                  ...PreviewCapturingStyle.flagBtnStyle,
                  padding: "unset",
                  justifyContent: variables.alignCenter,
                }}
                ghost
                icon={
                  <img
                    style={PreviewCapturingStyle.iconStyle}
                    src={"/icons/camera_switch.svg"}
                    alt="flag"
                  />
                }
                onClick={() => {
                  setFacingMode((prev) =>
                    prev === FACING_MODE.USER
                      ? FACING_MODE.ENVIRONMENT
                      : FACING_MODE.USER
                  );
                }}
              ></Button>
            )}
            {isWebStream && (
              <Button
                onClick={captureMediaHandleClick}
                style={PreviewCapturingStyle.mainBtnStyle}
                icon={null}
                disabled={disableButton}
              >
                <img
                  src={
                    isVideoCapturing
                      ? "/icons/Vector.svg"
                      : "/icons/photo_cameraNew.svg"
                  }
                  alt="cam"
                />
                {responsive.isSm &&
                  getLocalizedMessage(
                    intl,
                    getMessageKey(isVideoCapturing, isRecording)
                  )}
              </Button>
            )}
          </div>
        )}
        {isRecording ? (
          <div style={PreviewCapturingStyle.videoRecordingContainerStyle}>
            <img
              alt="close"
              src={"/icons/recording.svg"}
              style={PreviewCapturingStyle.recordingIconStyle}
            />
            {`${getLocalizedMessage(
              intl,
              "evidence.videoRecording"
            )} | ${formatDuration(duration)}`}
          </div>
        ) : (
          <div
            style={{
              ...PreviewCapturingStyle.upperWrapperStyle,
              ...(preview && {
                justifyContent: "flex-end",
              }),
            }}
          >
            {!preview && (
              <Text
                style={{
                  color: variables.bodyBg,
                  fontSize: variables.fontSizeNormal,
                  lineHeight: variables.lineHeightNormal,
                }}
              >
                {getLocalizedMessage(
                  intl,
                  getConditionalContent({
                    condition: isVideoCapturing,
                    trueValue: "evidence.recordVideo",
                    falseValue: "evidence.takeSnapshot",
                  })
                )}
              </Text>
            )}
            <img
              alt="close"
              src={getConditionalContent({
                condition: disableButton,
                trueValue: "/icons/close.svg",
                falseValue: "/icons/closeWhite.svg",
              })}
              style={PreviewCapturingStyle.closeIconStyle}
              onClick={() => {
                if (preview) setPreview(false);
                setStartCamera(false);
              }}
            />
          </div>
        )}
      </Modal>
      {disableButton && (
        <ConfirmModalContainer
          hideButton
          question={getLocalizedMessage(intl, "provideCamAccess")}
          open={disableButton || openModal}
          setOpen={setOpenModal}
          iconSrc={"/icons/warningIcon.svg"}
          onClose={() => {
            setOpenModal(false);
            setStartCamera(false);
          }}
        />
      )}
    </>
  );
};

export default PreviewCapturing;

function getMessageKey(isVideoCapturing, isRecording) {
  if (isVideoCapturing) {
    return isRecording
      ? "evidence.stopRecording"
      : "evidence.startVideoRecording";
  } else {
    return "evidence.takeSnapshot";
  }
}
